import React, { Component, useContext, useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import IGetOrdersByProductByCustomerDTO from "../../../../models/DTO/IGetOrdersByProductByCustomerDTO";
import IOnGetOrdersByProductByCustomerDTO from "../../../../models/DTO/IOnGetOrdersByProductByCustomerDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import ICatalog_Item from "../../../../models/ICatalog_Item";
import IProductsByCustomer from "../../../../models/IProductsByCustomer";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import ReportPeriodType from "../../../../models/ReportPeriodTypeEnum";
import ReportTypeEnum from "../../../../models/ReportTypeEnum";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import "./CustomerProductSalesReport.scss";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import ExcelJS from "exceljs";
import moment from "moment-timezone";
import SupplierContext from "../../../../contexts/SupplierContext";
import UserContext from "../../../../contexts/UserContext";
import { OwFnTable } from "../../../shared/OwFnTable/OwFnTable";
import IOwTableCols from "../../../../models/IOwTableCols";
import UiContext from "../../../../contexts/UiContext";
import SupplierService from "../../../../services/SupplierService";
import UIService from "../../../../services/UIService";

type Props = {
    processedReport: IProcessed_Report;
};

interface IGlobalTotals {
    qtyTotal: number;
    netTotal: number;
    costTotal: number;
    marginTotal: number;
}

export const CustomerProductSalesReport = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const uiContext = useContext(UiContext);

    const reportData = props.processedReport.report_data as IOnGetOrdersByProductByCustomerDTO;
    const vendors = reportData.vendors;
    const [globalTotals, setGlobalTotals] = useState<IGlobalTotals | undefined>();
    const prefix = "$";

    const processReportData = () => {
        const _globalTotals: IGlobalTotals = {
            qtyTotal: 0,
            netTotal: 0,
            costTotal: 0,
            marginTotal: 0,
        };

        for (const vendor of vendors) {
            for (const item of vendor.items) {
                _globalTotals.qtyTotal += item.qty;
                _globalTotals.netTotal += item.net;
                _globalTotals.costTotal += item.cost;
                _globalTotals.marginTotal += item.margin;
            }
        }

        setGlobalTotals(_globalTotals);
    };

    useEffect(() => {
        processReportData();
    }, []);

    const downloadExcel = () => {
        if (props.processedReport) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet("Customer Product Sales");

            const salesPeopleWs: { salesperson: string; ws: ExcelJS.Worksheet }[] = [];

            ws.columns = [
                { header: "Customer", key: "customer" },
                { header: "SKU", key: "sku" },
                { header: "Product", key: "product" },
                { header: "Qty", key: "qty" },
                { header: "Net", key: "net" },
            ];

            const headerCells = ["A1", "B1", "C1", "D1", "E1"];

            for (const cell of headerCells) {
                ws.getCell(cell).font = {
                    bold: true,
                };
            }

            const vendorRows = Object.keys(vendors);

            for (const vendor of vendors) {
                const name = vendor.name || " ";

                for (const item of vendor.items) {
                    const catalogItem: ICatalog_Item = item.catalog_item;
                    const qty = item.qty || " ";
                    const net = item.net || " ";

                    ws.addRow({ customer: name, sku: catalogItem.sku || " ", product: catalogItem.name || " ", qty, net });
                }

                const vendorWs = workbook.addWorksheet(name);

                vendorWs.columns = [
                    { header: "SKU", key: "sku" },
                    { header: "Product", key: "product" },
                    { header: "Qty", key: "qty" },
                    { header: "Net", key: "net" },
                ];

                const vendorHeaderCells = ["A1", "B1", "C1", "D1"];

                for (const cell of vendorHeaderCells) {
                    vendorWs.getCell(cell).font = {
                        bold: true,
                    };
                }

                let totalQty = 0;
                let totalNet = 0;

                for (const item of vendor.items) {
                    const catalogItem: ICatalog_Item = item.catalog_item;
                    const qty = item.qty;
                    const net = item.net;

                    vendorWs.addRow({
                        sku: catalogItem.sku || " ",
                        product: catalogItem.name || " ",
                        qty,
                        net,
                    });

                    totalQty += qty;
                    totalNet += net;
                }

                vendorWs.addRow({ sku: "", product: "Total", qty: totalQty, net: totalNet });
            }

            const periodStart = moment(props.processedReport.period_start).format("YYYY-MM-DD");
            const periodEnd = moment(props.processedReport.period_end).format("YYYY-MM-DD");

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `${periodStart}-${periodEnd}-orderwerks-customer-product-sales-report.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    return (
        <div className={"CustomerProductSalesReport"}>
            <div className="row">
                <OwTitle className={"col-12"} title={"customer_product_sales_report"} tag={"h3"} />
            </div>

            <div>
                <OwLabel className={"me-3"} title={"excel"} leftIconClass={"fas fa-download"} fn={downloadExcel} />
            </div>

            <div className={"card mb-4 p-2 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                <div className="row">
                    <div className="col-3">
                        <OwLabel title={"grand_total"} />
                    </div>
                    <div className={"col-2"}></div>
                    <div className={"col-1 text-end"}>
                        <NumericFormat
                            value={globalTotals?.qtyTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={prefix}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                        />
                    </div>
                    <div className={"col-2 text-end"}>
                        <NumericFormat
                            value={globalTotals?.netTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={prefix}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                        />
                    </div>
                    {supplierModel.has_cost_pricing ? (
                        <div className={"col-2 text-end"}>
                            <NumericFormat
                                value={globalTotals?.costTotal}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={prefix}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                            />
                        </div>
                    ) : null}
                    {supplierModel.has_cost_pricing ? (
                        <div className={"col-2 text-end"}>
                            <NumericFormat
                                value={(globalTotals?.marginTotal || 0) * 100}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                            />
                            %
                        </div>
                    ) : null}
                </div>
            </div>

            {vendors.map((vendor) => {
                let totalQty = 0;
                let totalNet = 0;
                let totalCost = 0;
                let totalMargin = 0;

                return (
                    <div key={vendor.guid} className={"card mb-4 p-2 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                        <div className="row">
                            <div className="col-3">
                                <OwLabel title={"customer"} isBold={true} isUnderline={true} />
                            </div>
                            <div className={"col-2"}>
                                <OwLabel title={"product"} isBold={true} isUnderline={true} />
                            </div>
                            <div className={"col-1 text-end"}>
                                <OwLabel title={"qty"} isBold={true} isUnderline={true} />
                            </div>
                            <div className={"col-2 text-end"}>
                                <OwLabel title={"net"} isBold={true} isUnderline={true} />
                            </div>
                            {supplierModel.has_cost_pricing ? (
                                <div className={"col-2 text-end"}>
                                    <OwLabel title={"cost"} isBold={true} isUnderline={true} />
                                </div>
                            ) : null}
                            {supplierModel.has_cost_pricing ? (
                                <div className={"col-2 text-end"}>
                                    <OwLabel title={"margin"} isBold={true} isUnderline={true} />
                                </div>
                            ) : null}
                        </div>
                        {vendor.items.map((item, index) => {
                            totalQty += item.qty;
                            totalNet += item.net;
                            totalCost += item.cost;
                            totalMargin += item.margin;

                            return (
                                <div key={item.catalog_item.guid} className="row">
                                    <div className="col-3">{index === 0 ? vendor.name : ""}</div>
                                    <div className={"col-2"}>{item.catalog_item.name}</div>
                                    <div className={"col-1 text-end"}>
                                        <NumericFormat
                                            value={item.qty}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            decimalScale={supplierModel.qty_precision || 2}
                                            fixedDecimalScale={true}
                                            prefix={""}
                                        />
                                    </div>
                                    <div className={"col-2 text-end"}>
                                        <NumericFormat
                                            value={item.net}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            prefix={prefix}
                                        />
                                    </div>
                                    {supplierModel.has_cost_pricing ? (
                                        <div className={"col-2 text-end"}>
                                            <NumericFormat
                                                value={item.cost}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                prefix={prefix}
                                            />
                                        </div>
                                    ) : null}
                                    {supplierModel.has_cost_pricing ? (
                                        <div className={"col-2 text-end"}>
                                            <NumericFormat
                                                value={item.margin * 100}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                            />
                                            %
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                        <div className={"row mt-2 mb-2"}>
                            <div className={"col-12"}>
                                <hr />
                            </div>
                        </div>
                        <div className={"row"}>
                            <div className="col-3">
                                <OwLabel title={"total"} noBottomMargin={true} />
                            </div>
                            <div className={"col-2"}></div>
                            <div className={"col-1 text-end"}>
                                <NumericFormat
                                    value={totalQty}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    decimalScale={supplierModel.qty_precision || 2}
                                    fixedDecimalScale={true}
                                    prefix={""}
                                />
                            </div>
                            <div className={"col-2 text-end"}>
                                <NumericFormat
                                    value={totalNet}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                    prefix={prefix}
                                />
                            </div>
                            {supplierModel.has_cost_pricing ? (
                                <div className={"col-2 text-end"}>
                                    <NumericFormat
                                        value={totalCost}
                                        displayType={"text"}
                                        thousandSeparator={true}
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                        prefix={prefix}
                                    />
                                </div>
                            ) : null}
                            {supplierModel.has_cost_pricing ? (
                                <div className={"col-2 text-end"}>
                                    <NumericFormat
                                        value={totalMargin * 100}
                                        displayType={"text"}
                                        thousandSeparator={true}
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                    />
                                    %
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
